<script setup>
import { computed } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { useToast } from '@/composables/useToast'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  projet:  { type: Object, default: () => ({}) },
  charges: { type: Array,  default: () => [] },
  errors:  { type: Object, default: () => ({}) },
})

const form = useForm({
  code:           props.projet.code          || '',
  nom:            props.projet.nom           || '',
  utilisateur_id: props.projet.utilisateur_id || '',
  description:    props.projet.description   || '',
  debut:          props.projet.debut         || '',
  fin:            props.projet.fin           || '',
  actif:          props.projet.actif ?? true,
})

function submit() {
  form.transform(data => ({
    projet: { ...data, utilisateur_id: data.utilisateur_id || null }
  })).patch(`/projets/${props.projet.id}`, {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

function cancel() {
  window.location.href = `/projets/${props.projet.id}`
}

// Rails renvoie "utilisateur" pour belongs_to, on mappe aussi utilisateur_id
const fieldError = (field) => {
  const aliases = { utilisateur_id: 'utilisateur' }
  const alt = aliases[field]
  return form.errors[field]
    || (props.errors[field]?.[0])
    || (alt && (form.errors[alt] || props.errors[alt]?.[0]))
    || null
}

const { error: toastError } = useToast()

const fieldLabels = {
  code:          'Ref / Code',
  nom:           'Désignation',
  debut:         'Date de début',
  fin:           'Date de fin',
  utilisateur:   'Chargé de projet',
  utilisateur_id:'Chargé de projet',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Administration &rsaquo; Modifier projet
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Modifier Projet
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ projet.code }}
            </p>
          </div>
          <a
            :href="`/projets/${projet.id}`"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Form Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Code + Nom -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Ref / Code <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  placeholder="ex. MON-042"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('code') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('code')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('code') }}</p>
              </div>
              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Désignation du projet <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.nom"
                  type="text"
                  placeholder="Nom officiel du projet"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('nom') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('nom')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('nom') }}</p>
              </div>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Description
              </label>
              <textarea
                v-model="form.description"
                rows="5"
                placeholder="Portée des travaux, livrables, contraintes..."
                class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
              ></textarea>
            </div>

            <!-- Chargé de projet + Actif -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Chargé de projet <span class="text-error">*</span>
                </label>
                <div class="relative">
                  <select
                    v-model="form.utilisateur_id"
                    class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 appearance-none transition-all outline-none"
                  >
                    <option value="">— Sélectionner —</option>
                    <option v-for="u in charges" :key="u.id" :value="u.id">{{ u.nom_complet }}</option>
                  </select>
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-outline material-symbols-outlined text-base">expand_more</span>
                </div>
              </div>
              <div class="flex items-center justify-between p-4 bg-surface-container-low rounded-md border border-outline-variant/10">
                <div>
                  <div class="text-sm font-bold text-on-surface">Statut actif</div>
                  <div class="text-[10px] text-outline mt-0.5">Activer / désactiver le projet</div>
                </div>
                <button
                  type="button"
                  @click="form.actif = !form.actif"
                  :class="[
                    'w-12 h-6 rounded-full relative transition-colors duration-200',
                    form.actif ? 'bg-tertiary/30' : 'bg-surface-container-highest'
                  ]"
                >
                  <div
                    :class="[
                      'absolute top-1 w-4 h-4 rounded-full shadow-sm transition-all duration-200',
                      form.actif ? 'right-1 bg-tertiary' : 'left-1 bg-outline/50'
                    ]"
                  ></div>
                </button>
              </div>
            </div>

            <!-- Dates -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-outline-variant/10">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Date de début <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.debut"
                  type="date"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('debut') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('debut')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('debut') }}</p>
              </div>
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Date de fin (estimée) <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.fin"
                  type="date"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('fin') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('fin')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('fin') }}</p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4 pt-6">
              <button
                type="submit"
                :disabled="form.processing"
                class="flex-1 machined-sheen text-on-primary font-black py-4 rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">save</span>
                Enregistrer les modifications
              </button>
              <button
                type="button"
                @click="cancel"
                class="px-8 py-4 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </button>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
